import React, { useCallback, useId } from "react";
import { styled } from "@mui/material/styles";
import { Select, MenuItem, Typography } from "@mui/material";
import { NODE_UI, stopPropagation } from "./nodeUiStyles";

/**
 * NodeSelect — standard dropdown for choosing from a fixed list of options.
 * Styled for on-canvas node config: compact, monotone, shadcn-inspired.
 *
 * Usage:
 *   <NodeSelect
 *     label="Output Format"
 *     value={format}
 *     onChange={(val) => setFormat(val)}
 *     options={[
 *       { value: 'json', label: 'JSON' },
 *       { value: 'csv', label: 'CSV' },
 *     ]}
 *   />
 */

const StyledSelect = styled(Select)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  borderRadius: NODE_UI.borderRadiusSm,
  backgroundColor: theme.palette.background.default,
  "& fieldset": {
    borderColor: theme.palette.divider,
  },
  "&:hover fieldset": {
    borderColor: theme.palette.text.secondary,
  },
  "&.Mui-focused fieldset": {
    borderColor: theme.palette.text.primary,
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: "4px",
  userSelect: "none",
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  padding: "6px 12px",
  "&.Mui-selected": {
    backgroundColor: theme.palette.action.selected,
  },
  "&.Mui-selected:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const menuProps = {
  PaperProps: {
    sx: (theme) => ({
      borderRadius: NODE_UI.borderRadiusSm,
      boxShadow: NODE_UI.shadow,
      border: `1px solid ${theme.palette.divider}`,
      marginTop: "2px",
    }),
  },
};

const NodeSelect = React.memo(function NodeSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  fullWidth = true,
  size = "small",
  sx,
  renderValue,
  displayEmpty = false,
}) {
  const handleChange = useCallback(
    (e) => {
      onChange?.(e.target.value);
    },
    [onChange]
  );
  const generatedId = useId();
  const labelId = `node-select-${generatedId}-label`;

  return (
    <div
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {label && <Label id={labelId}>{label}</Label>}
      <StyledSelect
        labelId={label ? labelId : undefined}
        inputProps={
          !label && placeholder ? { "aria-label": placeholder } : undefined
        }
        value={value ?? ""}
        onChange={handleChange}
        variant="outlined"
        displayEmpty={displayEmpty || !!placeholder}
        renderValue={
          renderValue ||
          (placeholder
            ? (selected) => {
                if (!selected && selected !== 0) {
                  return (
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: NODE_UI.fontSize.value,
                      }}
                    >
                      {placeholder}
                    </Typography>
                  );
                }
                const option = options.find((o) => o.value === selected);
                return option?.label ?? selected;
              }
            : undefined)
        }
        MenuProps={menuProps}
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        sx={sx}
      >
        {options.map((opt) => (
          <StyledMenuItem
            key={opt.value}
            value={opt.value}
            disabled={opt.disabled}
          >
            {opt.label}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </div>
  );
});

export default NodeSelect;
